import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { RosterAvailabilityUpsertBody } from '../types'

// Per-event RosterAvailability hooks (mig 041, PR R3).
//
//   - useMyAvailability   — caller's rows; powers the availability page
//                            in self-edit mode AND the cell picker's
//                            "Only available" filter when the caller is
//                            not an admin.
//   - useEventAvailability — every user's rows (admin only). Powers the
//                            cell picker's "Only available" filter for
//                            admins, and the availability page when an
//                            admin marks on behalf of someone else.
//   - useUpsertAvailability — batch save from the availability page.
//                              Invalidates both reads + the roster
//                              query (the picker reads availability
//                              through these caches).

export function useMyAvailability(eventId: number) {
  return useQuery({
    queryKey: ['availability', eventId, 'me'],
    queryFn: () => ApiClient.myAvailability(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

// Admin-only. The query function returns 403 for non-admin callers, so
// gate the call upstream via me.isAdmin before mounting components
// that consume this. `enabled` accepts the gate.
export function useEventAvailability(eventId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['availability', eventId, 'all'],
    queryFn: () => ApiClient.eventAvailability(eventId),
    enabled: enabled && Number.isFinite(eventId) && eventId > 0
  })
}

export function useUpsertAvailability(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RosterAvailabilityUpsertBody) =>
      ApiClient.upsertAvailability(eventId, body),
    onSuccess: () => {
      // Both reads share state; invalidate both. The roster GET is
      // unaffected, but the cell picker filters off these caches so
      // chip-add-flows see fresh availability on next open.
      qc.invalidateQueries({ queryKey: ['availability', eventId] })
    }
  })
}
